import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminService, type ReportConfig, type ScheduleConfig } from '@/lib/api/services/admin.service'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Users,
  Building2,
  MessageSquare,
  TrendingUp,
  Send,
  Plus,
  X,
  Mail,
  Loader2,
  Info,
  FileText
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts'
import { ScheduleConfigForm } from './ScheduleConfigForm'
import { ReportPreviewDialog } from './ReportPreviewDialog'

interface PlatformAnalyticsProps {
  startDate?: string
  endDate?: string
}

// Stat card matching the app design
function StatCard({
  icon: Icon,
  value,
  label,
  subtext,
  tooltip,
  iconBgColor = 'bg-[rgba(70,105,237,0.10)]',
  iconColor = 'text-[#4669ed]'
}: {
  icon: React.ComponentType<{ className?: string }>
  value: string | number
  label: string
  subtext?: string
  tooltip: string
  iconBgColor?: string
  iconColor?: string
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="bg-white rounded-3xl border border-slate-200 p-6 flex flex-col items-center justify-center cursor-help shadow-sm hover:shadow-md transition-shadow min-h-[180px]">
            <div className={cn("w-14 h-14 rounded-full flex items-center justify-center mb-3", iconBgColor)}>
              <Icon className={cn("w-6 h-6", iconColor)} />
            </div>
            <div className="text-4xl font-semibold text-slate-900 tracking-tight">
              {value}
            </div>
            <div className="text-sm text-slate-500 mt-2 flex items-center gap-1">
              {label}
              <Info className="w-3 h-3 text-slate-400" />
            </div>
            {subtext && (
              <div className="text-xs text-slate-400 mt-1">
                {subtext}
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Colors for charts
const CHART_COLORS = {
  primary: '#4669ed',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  slate: '#64748b'
}

const PIE_COLORS = ['#4669ed', '#8b5cf6', '#10b981', '#f59e0b']

export function PlatformAnalytics({ startDate, endDate }: PlatformAnalyticsProps) {
  const queryClient = useQueryClient()
  const [newEmail, setNewEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Fetch analytics data
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['platform-analytics', startDate, endDate],
    queryFn: () => adminService.getPlatformAnalytics(startDate, endDate),
  })

  // Fetch report config
  const { data: reportConfig } = useQuery({
    queryKey: ['report-config'],
    queryFn: () => adminService.getReportConfig(),
  })

  // Fetch schedule config
  const { data: scheduleConfig, isLoading: scheduleLoading } = useQuery({
    queryKey: ['schedule-config'],
    queryFn: () => adminService.getScheduleConfig(),
  })

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: (updates: Partial<ReportConfig>) => adminService.updateReportConfig(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-config'] })
    },
  })

  // Update schedule config mutation
  const updateScheduleConfigMutation = useMutation({
    mutationFn: (updates: Partial<ScheduleConfig>) => adminService.updateScheduleConfig(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-config'] })
      queryClient.invalidateQueries({ queryKey: ['report-config'] }) // Schedule affects report config display
      showSaveMessage('success', 'Schedule updated')
    },
    onError: () => {
      showSaveMessage('error', 'Failed to update schedule')
    },
  })

  // Send report mutation
  const sendReportMutation = useMutation({
    mutationFn: () => adminService.sendReportNow(),
    onSuccess: () => {
      showSaveMessage('success', 'Report sent to all recipients')
    },
    onError: () => {
      showSaveMessage('error', 'Failed to send report')
    },
  })

  const handleAddEmail = async () => {
    if (!reportConfig || !newEmail.trim()) return

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newEmail.trim())) {
      setEmailError('Please enter a valid email address')
      return
    }

    if (reportConfig.recipients.includes(newEmail.trim())) {
      setEmailError('This email is already added')
      return
    }

    try {
      await updateConfigMutation.mutateAsync({
        recipients: [...reportConfig.recipients, newEmail.trim()]
      })
      setNewEmail('')
      setEmailError('')
      showSaveMessage('success', 'Recipient added')
    } catch {
      showSaveMessage('error', 'Failed to add recipient')
    }
  }

  const handleRemoveEmail = async (email: string) => {
    if (!reportConfig) return
    try {
      await updateConfigMutation.mutateAsync({
        recipients: reportConfig.recipients.filter(e => e !== email)
      })
      showSaveMessage('success', 'Recipient removed')
    } catch {
      showSaveMessage('error', 'Failed to remove recipient')
    }
  }

  const handleSendNow = async () => {
    // Use the mutation directly - onSuccess/onError handlers will show messages
    sendReportMutation.mutate()
  }

  const showSaveMessage = (type: 'success' | 'error', text: string) => {
    // Clear any existing timeout to prevent stacking
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setSaveMessage({ type, text })
    timeoutRef.current = setTimeout(() => setSaveMessage(null), 3000)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Prepare chart data
  const userDistributionData = analytics ? [
    { name: 'Geniuses', value: analytics.totalContractors, color: CHART_COLORS.primary },
    { name: 'Clients', value: analytics.totalBusinesses, color: CHART_COLORS.secondary },
  ] : []

  const messagesBarData = analytics?.messagesPerGenius?.slice(0, 8).map(g => {
    const displayName = g.displayName || 'Unknown'
    return {
      name: displayName.length > 12 ? displayName.substring(0, 12) + '...' : displayName,
      fullName: displayName,
      messages: g.messageCount || 0
    }
  }) || []

  const connectionRateData = analytics ? [
    { name: 'Connected', value: analytics.totalClientsWhoMessaged, color: CHART_COLORS.success },
    { name: 'Not Connected', value: Math.max(0, analytics.totalActiveClients - analytics.totalClientsWhoMessaged), color: CHART_COLORS.slate },
  ] : []

  if (analyticsLoading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          value={analytics?.newContractors || 0}
          label="New Geniuses"
          subtext={`Total: ${analytics?.totalContractors || 0}`}
          tooltip="Number of new genius (freelancer) accounts created within the selected date range"
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          icon={Building2}
          value={analytics?.newBusinesses || 0}
          label="New Clients"
          subtext={`Total: ${analytics?.totalBusinesses || 0}`}
          tooltip="Number of new client (business) accounts created within the selected date range"
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          icon={MessageSquare}
          value={analytics?.totalMessagesSent || 0}
          label="Messages Sent"
          subtext={`Ratio (B:C): ${analytics?.businessToContractorRatio || 0}`}
          tooltip="Total messages sent in the period. Ratio shows business messages vs contractor messages"
          iconBgColor="bg-emerald-100"
          iconColor="text-emerald-600"
        />
        <StatCard
          icon={TrendingUp}
          value={`${analytics?.connectionRate || 0}%`}
          label="Connection Rate"
          subtext={`${analytics?.totalClientsWhoMessaged || 0} of ${analytics?.totalActiveClients || 0} clients`}
          tooltip="Percentage of clients who have initiated a conversation with a genius"
          iconBgColor="bg-amber-100"
          iconColor="text-amber-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User Distribution Pie Chart */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-1">User Distribution</h3>
          <p className="text-sm text-slate-500 mb-4">Geniuses vs Clients</p>

          {userDistributionData.some(d => d.value > 0) ? (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={userDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {userDistributionData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
              No user data available
            </div>
          )}
        </div>

        {/* Connection Rate Pie Chart */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-1">Connection Rate</h3>
          <p className="text-sm text-slate-500 mb-4">Clients who messaged a genius</p>

          {connectionRateData.some(d => d.value > 0) ? (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={connectionRateData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {connectionRateData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
              No connection data available
            </div>
          )}
        </div>

        {/* Weekly Report Settings - Full Config */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#4669ed]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Report Schedule</h3>
              <p className="text-xs text-slate-500">Configure automated reports</p>
            </div>
          </div>

          {scheduleLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : scheduleConfig ? (
            <ScheduleConfigForm
              config={scheduleConfig}
              onUpdate={(updates) => updateScheduleConfigMutation.mutate(updates)}
              isUpdating={updateScheduleConfigMutation.isPending}
            />
          ) : (
            <div className="text-center py-4 text-slate-400 text-sm">
              Failed to load schedule
            </div>
          )}
        </div>
      </div>

      {/* Messages Per Genius Bar Chart */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Top Active Geniuses</h3>
            <p className="text-sm text-slate-500">Messages sent this period</p>
          </div>
          {analytics?.messagesPerGenius && analytics.messagesPerGenius.length > 8 && (
            <span className="text-xs text-slate-400">Showing top 8</span>
          )}
        </div>

        {messagesBarData.length > 0 ? (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={messagesBarData} layout="vertical" margin={{ left: 20, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={100}
                />
                <RechartsTooltip
                  contentStyle={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value) => [`${value} messages`, 'Messages']}
                />
                <Bar
                  dataKey="messages"
                  fill={CHART_COLORS.primary}
                  radius={[0, 8, 8, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[280px] flex flex-col items-center justify-center text-slate-400">
            <MessageSquare className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-sm">No message activity this period</p>
          </div>
        )}
      </div>

      {/* Email Recipients Management */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Report Recipients</h3>
            <p className="text-sm text-slate-500">Manage email addresses for reports</p>
          </div>
        </div>

        {reportConfig && (
          <div className="space-y-4">
            {/* Add Email */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value)
                    setEmailError('')
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                  className={cn(
                    "rounded-xl h-11",
                    emailError && 'border-red-500 focus-visible:ring-red-500'
                  )}
                />
                {emailError && (
                  <p className="text-xs text-red-500 mt-1 ml-1">{emailError}</p>
                )}
              </div>
              <Button
                onClick={handleAddEmail}
                disabled={updateConfigMutation.isPending || !newEmail.trim()}
                className="rounded-xl h-11 px-4 bg-[#4669ed] hover:bg-[#3a5ad9]"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Recipients List */}
            {reportConfig.recipients.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {reportConfig.recipients.map((email) => (
                  <div
                    key={email}
                    className="flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 text-sm text-slate-700"
                  >
                    <span>{email}</span>
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      disabled={updateConfigMutation.isPending}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">
                No recipients configured. Add an email to receive reports.
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => setReportDialogOpen(true)}
                disabled={!analytics}
                className="flex-1 rounded-xl h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
                variant="outline"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
              <Button
                onClick={handleSendNow}
                disabled={sendReportMutation.isPending || reportConfig.recipients.length === 0}
                className="flex-1 rounded-xl h-11 bg-[#4669ed] hover:bg-[#3a5ad9] text-white font-medium"
              >
                {sendReportMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Now
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {saveMessage && (
        <div className={cn(
          "fixed bottom-6 right-6 px-5 py-3 rounded-xl shadow-lg transition-all z-50 font-medium",
          saveMessage.type === 'success'
            ? "bg-emerald-500 text-white"
            : "bg-red-500 text-white"
        )}>
          {saveMessage.text}
        </div>
      )}

      {/* Report Preview Dialog */}
      <ReportPreviewDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        analytics={analytics || null}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  )
}
